import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ROLE_LABEL, SEMANTIC_DOT } from "@/lib/meta";
import {
  LayoutDashboard, Users, CalendarDays, Send, ListChecks, Camera,
  Wallet, Building2, LogOut, Bell, Menu, LayoutGrid, SlidersHorizontal,
  TrendingUp, Palette, Video, Activity, Home, CalendarHeart, DatabaseBackup,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

function buildNav(user) {
  const role = user?.role;
  const job = user?.job_role;
  const canPay = user?.can_view_payments;
  const special = { to: "/special-days", label: "Special Days", icon: CalendarHeart };

  if (role === "super_admin" || role === "manager") {
    return [
      { to: "/", label: "Dashboard", icon: LayoutDashboard },
      { to: "/team-progress", label: "Team Progress", icon: TrendingUp },
      { to: "/clients", label: "Clients", icon: Building2 },
      { to: "/calendar", label: "Content Calendar", icon: CalendarDays },
      special,
      { to: "/publishing", label: "Publishing", icon: Send },
      { to: "/design-pool", label: "Design Pool", icon: Palette },
      { to: "/video-pool", label: "Video Pool", icon: Video },
      { to: "/tasks", label: "Tasks", icon: ListChecks },
      { to: "/shoots", label: "Shoots", icon: Camera },
      { to: "/payments", label: "Payments", icon: Wallet },
      { to: "/activity", label: "Activity", icon: Activity },
      { to: "/team", label: "Team", icon: Users },
      { to: "/backups", label: "Backups", icon: DatabaseBackup },
    ];
  }

  // employees — role-specific navigation
  const home = { to: "/", label: "My Workspace", icon: Home };
  const tasks = { to: "/tasks", label: "My Tasks", icon: ListChecks };
  if (job === "social_media_handler") {
    return [home, { to: "/clients", label: "My Clients", icon: Building2 }, { to: "/publishing", label: "Publishing", icon: Send }, special, tasks, { to: "/shoots", label: "Shoots", icon: Camera }];
  }
  if (job === "designer") {
    return [{ to: "/", label: "Design Pool", icon: Palette }, special, tasks];
  }
  if (job === "video_editor") {
    return [{ to: "/", label: "Video Pool", icon: Video }, special, tasks];
  }
  if (job === "account_manager") {
    const nav = [home, { to: "/clients", label: "My Clients", icon: Building2 }, special, tasks, { to: "/shoots", label: "Shoots", icon: Camera }];
    if (canPay) nav.push({ to: "/payments", label: "Payments", icon: Wallet });
    return nav;
  }
  return [home, special, tasks];
}

function NavItems({ user, onClick }) {
  const items = buildNav(user);
  return (
    <nav className="flex flex-col gap-0.5 px-3">
      {items.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === "/"}
          onClick={onClick}
          data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              isActive ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
            }`
          }
        >
          <n.icon className="h-[18px] w-[18px]" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-6 py-5">
      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-zinc-900 text-white">
        <LayoutGrid className="h-[18px] w-[18px]" />
      </div>
      <div className="leading-tight">
        <div className="font-heading text-sm font-bold tracking-tight">CLASSIC24 OS</div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-400">Agency Operations</div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    api.get("/notifications").then(({ data }) => setNotes(data)).catch(() => {});
  }, []);

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex min-h-screen bg-zinc-50">
      {/* desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-zinc-200 bg-white lg:flex">
        <Brand />
        <div className="flex-1 overflow-y-auto py-2">
          <NavItems user={user} />
        </div>
        <div className="border-t border-zinc-200 p-3">
          <div className="flex items-center gap-3 rounded-md px-3 py-2">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white"
              style={{ background: user?.color || "#09090b" }}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-sm font-medium">{user?.name}</div>
              <div className="truncate text-xs text-zinc-400">{user?.title || ROLE_LABEL[user?.role]}</div>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex flex-1 flex-col lg:pl-64">
        {/* topbar */}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-zinc-200 bg-white/90 px-4 backdrop-blur lg:px-8">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="rounded-md p-2 hover:bg-zinc-100 lg:hidden" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <Brand />
                <NavItems user={user} onClick={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="font-heading text-base font-semibold tracking-tight lg:hidden">CLASSIC24 OS</div>
          </div>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative rounded-md p-2 hover:bg-zinc-100" data-testid="notifications-button">
                  <Bell className="h-5 w-5 text-zinc-600" />
                  {notes.length > 0 && (
                    <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red-500" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notes.length === 0 && <div className="px-2 py-6 text-center text-sm text-zinc-400">All clear.</div>}
                {notes.map((n, i) => (
                  <div key={i} className="flex items-start gap-2 px-2 py-2 text-sm" data-testid={`notification-${i}`}>
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEMANTIC_DOT[n.type]}`} />
                    <span className="text-zinc-700">{n.message}</span>
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white"
                  style={{ background: user?.color || "#09090b" }}
                  data-testid="user-menu-button"
                >
                  {initials}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.name}</div>
                  <div className="text-xs font-normal text-zinc-400">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(user?.role === "super_admin" || user?.role === "manager") && (
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-menu-item">
                    <SlidersHorizontal className="mr-2 h-4 w-4" /> Settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => { logout(); navigate("/login"); }}
                  data-testid="logout-button"
                  className="text-red-600 focus:text-red-600"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
